import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";
import { Mail, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

export default function FacultyPage({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Faculty | Bandarban University",
}) {
	const [faculties, setFaculties] = useState([]);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const faculty = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },				
			});
			const response = await api.post("/all-teacher", payload);
			
			console.log(response.data);
			// Loop data (array)
			setFaculties(response.data || []);
			
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	
	useEffect(() => {
		faculty();
	}, []);

	const filtered = faculties.filter((f) =>
		f.name.toLowerCase().includes(search.toLowerCase())
	);
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
	
	return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-600 to-teal-500 text-white py-20 px-6 text-center">
        <motion.h1
          className="text-4xl md:text-5xl font-bold mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Meet Our Faculty
        </motion.h1>
        <motion.p
          className="max-w-2xl mx-auto text-lg opacity-90"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Explore the brilliant minds shaping the future of our students.
        </motion.p>

        {/* Search Bar */}
        <div className="max-w-lg mx-auto mt-8">
          <input
            type="text"
            placeholder="Search Faculty by Name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 shadow-lg text-gray-700 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
          />
        </div>
      </section>

      {/* Faculty List */}
     <section className="max-w-7xl mx-auto py-12 px-6">
		  <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
			{filtered.map((faculty, i) => (
			  <motion.div
				key={faculty.id}
				initial={{ opacity: 0, y: 40 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ delay: i * 0.08 }}
				whileHover={{
				  y: -10,
				  scale: 1.03,
				}}
				className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
			  >
				{/* Image Section */}
				<div className="relative bg-gradient-to-br from-green-50 via-white to-orange-50 h-72 flex items-center justify-center overflow-hidden">
				  
				  {/* Decorative Circle */}
				  <div className="absolute w-52 h-52 bg-green-100 rounded-full -top-16 -right-16 opacity-50"></div>

				  <img
					src={`${import.meta.env.VITE_API_SOURCE_URL}${
					  faculty.picture
						? faculty.picture
						: "assets/images/teacher.jpg"
					}`}
					alt={faculty.name}
					className="relative z-10 max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
				  />
				</div>

				{/* Content */}
				<div className="p-5">
				  <h3 className="text-xl font-bold text-gray-800 line-clamp-2">
					{faculty.name}
				  </h3>

				  <p className="mt-1 text-green-700 font-medium">
					{faculty.designation_name}
				  </p>

				  <div className="mt-2 inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold">
					{faculty.department_name}
				  </div>

				  <div className="mt-4 space-y-2 text-sm text-gray-600">
					{faculty.mobile_number && (
					  <p className="flex items-center gap-2">
						📞 <span>{faculty.mobile_number}</span>
					  </p>
					)}

					{faculty.email && (
					  <p className="flex items-center gap-2 break-all">
						✉️ <span>{faculty.email}</span>
					  </p>
					)}

					{faculty.university_email && (
					  <p className="flex items-center gap-2 break-all">
						🎓 <span>{faculty.university_email}</span>
					  </p>
					)}
				  </div>

				  <Link
					to={`/profile/${faculty.teacher_id}`}
					className="mt-5 block w-full text-center py-3 rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold hover:from-orange-500 hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg"
				  >
					View Profile
				  </Link>
				</div>
			  </motion.div>
			))}
		  </div>
		</section>
    </div>
  );
}
